import { getObjectDiff } from '@hourly/utilities';
import {
  Button,
  ButtonVariant,
  MaskedInput,
  SingleSelect,
  TextInput,
} from '@hourly-hds/components';
import { useEffect, useState } from 'react';
import z from 'zod';
import { US_STATES_OPTIONS } from '~/constants/states';
import { TabContentSpinner } from '../../../_shared/components/TabContentSpinner';
import { usePartnerDetails } from './data/hooks/usePartnerDetails';
import { TaxPayerIdentificationType } from './data/models/partner.model';
import styles from './PartnerInformation.module.css';

const taxpayerMask: Record<TaxPayerIdentificationType, string> = {
  [TaxPayerIdentificationType.EmployerIdentificationNumber]: '00-0000000',
  [TaxPayerIdentificationType.SocialSecurityNumber]: '000-00-0000',
} as const;

type FormErrors<T> = {
  [K in keyof T]?: T[K] extends object ? FormErrors<T[K]> : string;
};

const partnerSchema = z.object({
  dba: z.string().optional(),
  address: z.object({
    address: z.string().min(1, { message: 'Address is required.' }),
    address2: z.string(),
    city: z.string().min(1, { message: 'City is required.' }),
    state: z.string().min(1, { message: 'State is required.' }),
    zipcode: z.string().min(1, { message: 'Zip code is required.' }),
  }),
});

type PartnerForm = z.infer<typeof partnerSchema>;

const initialPartnerForm: PartnerForm = {
  address: {
    address: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
  },
  dba: '',
};

export default function PartnerInformation({ partnerId }: { partnerId: string }) {
  const { partner, isFetchPartnerDetailsSettled, editPartner } = usePartnerDetails(partnerId);
  const [errors, setErrors] = useState<FormErrors<PartnerForm>>({});

  const [partnerInfo, setPartnerInfo] = useState<PartnerForm>({
    ...initialPartnerForm,
  });

  useEffect(() => {
    if (partner) {
      setErrors({});
      setPartnerInfo((prev) => ({ ...prev, ...partner }));
    }
  }, [partner]);

  if (!isFetchPartnerDetailsSettled) {
    return <TabContentSpinner />;
  }

  if (!partner) {
    return <div>Partner not found</div>;
  }

  const onSave = () => {
    const result = partnerSchema.safeParse(partnerInfo);
    if (!result.success) {
      const tree = z.treeifyError(result.error);
      const properties = tree.properties ?? {};

      setErrors({
        address: {
          address: properties?.address?.properties?.address?.errors[0],
          city: properties?.address?.properties?.city?.errors[0],
          state: properties?.address?.properties?.state?.errors[0],
          zipcode: properties?.address?.properties?.zipcode?.errors[0],
        },
      });
    } else {
      const partnerToUpdate = {
        dba: (result.data.dba || '').trim(),
        address: {
          address: result.data.address.address.trim(),
          address2: result.data.address.address2.trim(),
          city: result.data.address.city.trim(),
          state: result.data.address.state,
          zipcode: result.data.address?.zipcode,
        },
      };
      editPartner(partnerToUpdate);
    }
  };

  const handleChangeInfo = (changes: Partial<PartnerForm>) => {
    setPartnerInfo((currentState) => ({ ...currentState, ...changes }));
  };

  const partnerToUpdate = {
    dba: (partnerInfo.dba || '').trim(),
    address: {
      address: partnerInfo.address.address.trim(),
      address2: partnerInfo.address.address2.trim(),
      city: partnerInfo.address.city.trim(),
      state: partnerInfo.address.state,
      zipcode: partnerInfo.address?.zipcode,
    },
  };
  const diff = getObjectDiff(partner, partnerToUpdate);
  const canSave = Object.keys(diff).length > 0;

  return (
    <div>
      <div className={styles.header}>
        <p className={styles.title}>Partner Information</p>
        <Button label="Save" variant={ButtonVariant.Primary} onClick={onSave} disabled={!canSave} />
      </div>

      <div className={styles.content}>
        <TextInput label="Partner Legal Name" value={partner.legalName} disabled />
        <TextInput
          label="DBA"
          value={partnerInfo.dba}
          onChange={(dba) => handleChangeInfo({ dba })}
        />
        <SingleSelect
          label="Federal Taxpayer Identification Type"
          options={[
            {
              value: partner.taxpayerIdentificationType,
              label: partner.taxpayerIdentificationType,
            },
          ]}
          value={partner.taxpayerIdentificationType}
          disabled
          description={null}
          error={null}
          onChange={() => {}}
        />
        <MaskedInput
          label={partner.taxpayerIdentificationType}
          mask={taxpayerMask[partner.taxpayerIdentificationType]}
          value={partner.taxpayerIdentificationNumber}
          disabled
        />
        <SingleSelect
          label="Federal Tax Classification"
          onChange={() => {}}
          options={[
            {
              value: partner.federalTaxClassification,
              label: partner.federalTaxClassification,
            },
          ]}
          value={partner.federalTaxClassification}
          disabled
          description={null}
          error={null}
        />
      </div>
      <div className={styles.content}>
        <TextInput
          label={'Physical Address'}
          value={partnerInfo.address?.address}
          onChange={(address) => handleChangeInfo({ address: { ...partnerInfo.address, address } })}
          description={errors.address?.address}
          error={!!errors.address?.address}
        />
        <TextInput
          label="Address Line 2 (Apt #, Suite #, etc)"
          value={partnerInfo.address?.address2}
          onChange={(address2) =>
            handleChangeInfo({ address: { ...partnerInfo.address, address2 } })
          }
          error={!!errors.address?.address2}
          description={errors.address?.address2}
        />
        <TextInput
          label="City"
          value={partnerInfo.address?.city}
          onChange={(city) => handleChangeInfo({ address: { ...partnerInfo.address, city } })}
          description={errors.address?.city}
          error={!!errors.address?.city}
        />
        <div className={styles.content}>
          <SingleSelect
            label="State"
            options={US_STATES_OPTIONS}
            value={partnerInfo.address?.state}
            onChange={(state) => handleChangeInfo({ address: { ...partnerInfo.address, state } })}
            description={errors.address?.state}
            error={!!errors.address?.state}
            disabled={false}
          />
          <MaskedInput
            label="Zip Code"
            mask="00000"
            value={partnerInfo.address?.zipcode}
            onChange={(zipcode) =>
              handleChangeInfo({ address: { ...partnerInfo.address, zipcode } })
            }
            description={errors.address?.zipcode}
            error={!!errors.address?.zipcode}
          />
        </div>
      </div>
    </div>
  );
}
